import {
  BrandCategoriesApiItemType,
  PurchaseTermsNamedItemType,
  RuleGroupConfigType,
  RuleGroupInactiveFilterItemType,
} from 'features/Enterprise/Vendor/types/PurchaseTerms'

export type BrandScopeSelectionType = {
  brands: PurchaseTermsNamedItemType[]
  l1Categories: RuleGroupInactiveFilterItemType[]
  l2Categories: RuleGroupInactiveFilterItemType[]
}

type CatalogPathType = {
  brand: PurchaseTermsNamedItemType
  l1: PurchaseTermsNamedItemType
  l2: PurchaseTermsNamedItemType | null
}

const uniqueNamedItems = (items: PurchaseTermsNamedItemType[]) => {
  const byId = new Map<number, PurchaseTermsNamedItemType>()
  items.forEach((item) => {
    byId.set(item.id, item)
  })
  return Array.from(byId.values())
}

const uniqueScopeFilterItems = (items: RuleGroupInactiveFilterItemType[]) => {
  const byId = new Map<number, RuleGroupInactiveFilterItemType>()
  items.forEach((item) => {
    const itemId = item.id
    if (itemId == null || itemId <= 0) return
    byId.set(itemId, { id: itemId, name: item.name })
  })
  return Array.from(byId.values())
}

const toNamedCategory = (category: {
  category_id: number
  category_name: string
}): PurchaseTermsNamedItemType => ({
  id: category.category_id,
  name: category.category_name,
})

const toBrandIdSet = (items: PurchaseTermsNamedItemType[]) => new Set(items.map((item) => item.id))

const toScopeIdSet = (items: RuleGroupInactiveFilterItemType[]) =>
  new Set(items.map((item) => item.id).filter((id): id is number => id != null && id > 0))

export const extractBrandOptions = (
  catalog: BrandCategoriesApiItemType[],
): PurchaseTermsNamedItemType[] =>
  uniqueNamedItems(
    catalog.map((item) => ({
      id: item.brand_id,
      name: item.brand_name,
    })),
  )

export const extractL1Options = (
  catalog: BrandCategoriesApiItemType[],
): PurchaseTermsNamedItemType[] => {
  const l1Items: PurchaseTermsNamedItemType[] = []

  catalog.forEach((brand) => {
    brand.categories.forEach((l1Category) => {
      l1Items.push(toNamedCategory(l1Category))
    })
  })

  return uniqueNamedItems(l1Items)
}

export const extractL2Options = (
  catalog: BrandCategoriesApiItemType[],
): PurchaseTermsNamedItemType[] => {
  const l2Items: PurchaseTermsNamedItemType[] = []

  catalog.forEach((brand) => {
    brand.categories.forEach((l1Category) => {
      const subcategories = l1Category.subcategories ?? []
      subcategories.forEach((l2Category) => {
        l2Items.push(toNamedCategory(l2Category))
      })
    })
  })

  return uniqueNamedItems(l2Items)
}

const buildCatalogPaths = (catalog: BrandCategoriesApiItemType[]): CatalogPathType[] => {
  const paths: CatalogPathType[] = []

  catalog.forEach((brand) => {
    const brandItem = { id: brand.brand_id, name: brand.brand_name }
    brand.categories.forEach((l1Category) => {
      const l1Item = toNamedCategory(l1Category)
      const subcategories = l1Category.subcategories ?? []
      if (subcategories.length === 0) {
        paths.push({ brand: brandItem, l1: l1Item, l2: null })
        return
      }
      subcategories.forEach((l2Category) => {
        paths.push({ brand: brandItem, l1: l1Item, l2: toNamedCategory(l2Category) })
      })
    })
  })

  return paths
}

const collectMatchingItems = (
  paths: CatalogPathType[],
  pickItem: (path: CatalogPathType) => PurchaseTermsNamedItemType | null,
  isMatch: (path: CatalogPathType) => boolean,
) => {
  const items: PurchaseTermsNamedItemType[] = []
  paths.forEach((path) => {
    if (!isMatch(path)) return
    const item = pickItem(path)
    if (item) items.push(item)
  })
  return uniqueNamedItems(items)
}

export const getCascadedScopeOptions = (
  catalog: BrandCategoriesApiItemType[],
  selected: BrandScopeSelectionType,
): BrandScopeSelectionType => {
  const paths = buildCatalogPaths(catalog)
  const selectedBrandIds = toBrandIdSet(selected.brands)
  const selectedL1Ids = toScopeIdSet(selected.l1Categories)
  const selectedL2Ids = toScopeIdSet(selected.l2Categories)
  const hasBrand = selectedBrandIds.size > 0
  const hasL1 = selectedL1Ids.size > 0
  const hasL2 = selectedL2Ids.size > 0

  const matchesBrandConstraint = (path: CatalogPathType) =>
    !hasBrand || selectedBrandIds.has(path.brand.id)

  const matchesL1Constraint = (path: CatalogPathType) => !hasL1 || selectedL1Ids.has(path.l1.id)

  const matchesL2Constraint = (path: CatalogPathType) =>
    !hasL2 || Boolean(path.l2 && selectedL2Ids.has(path.l2.id))

  return {
    brands: collectMatchingItems(
      paths,
      (path) => path.brand,
      (path) => matchesL1Constraint(path) && matchesL2Constraint(path),
    ),
    l1Categories: collectMatchingItems(
      paths,
      (path) => path.l1,
      (path) => matchesBrandConstraint(path),
    ),
    l2Categories: collectMatchingItems(
      paths,
      (path) => path.l2,
      (path) => matchesBrandConstraint(path) && matchesL1Constraint(path),
    ),
  }
}

export const getVisibleScopeOptions = (
  catalog: BrandCategoriesApiItemType[],
  selected: BrandScopeSelectionType,
): BrandScopeSelectionType => {
  const cascaded = getCascadedScopeOptions(catalog, selected)
  return {
    brands: uniqueNamedItems([...cascaded.brands, ...selected.brands]),
    l1Categories: uniqueScopeFilterItems([...cascaded.l1Categories, ...selected.l1Categories]),
    l2Categories: uniqueScopeFilterItems([...cascaded.l2Categories, ...selected.l2Categories]),
  }
}

export const isBrandScopeRelatedInCatalog = (
  catalog: BrandCategoriesApiItemType[],
  selection: BrandScopeSelectionType,
): boolean => {
  const hasL1 = selection.l1Categories.length > 0
  const hasL2 = selection.l2Categories.length > 0
  if (!hasL1 && !hasL2) return true

  const paths = buildCatalogPaths(catalog)
  const selectedBrandIds = toBrandIdSet(selection.brands)
  const selectedL1Ids = toScopeIdSet(selection.l1Categories)
  const selectedL2Ids = toScopeIdSet(selection.l2Categories)
  const hasBrand = selectedBrandIds.size > 0

  if (hasL1) {
    const allL1Related = Array.from(selectedL1Ids).every((l1Id) =>
      paths.some(
        (path) => path.l1.id === l1Id && (!hasBrand || selectedBrandIds.has(path.brand.id)),
      ),
    )
    if (!allL1Related) return false
  }

  if (hasL2) {
    const allL2Related = Array.from(selectedL2Ids).every((l2Id) =>
      paths.some(
        (path) =>
          Boolean(path.l2 && path.l2.id === l2Id) &&
          (!hasBrand || selectedBrandIds.has(path.brand.id)) &&
          (!hasL1 || selectedL1Ids.has(path.l1.id)),
      ),
    )
    if (!allL2Related) return false
  }

  return true
}

export const brandScopeConfigKey = (config: RuleGroupConfigType) =>
  `${config.brand_id ?? 0}:${config.l1_category_id ?? 0}:${config.l2_category_id ?? 0}`

const buildConfigTuple = (
  brand: PurchaseTermsNamedItemType,
  l1Category: PurchaseTermsNamedItemType | null,
  l2Category: PurchaseTermsNamedItemType | null,
): RuleGroupConfigType => ({
  id: 0,
  brand_id: brand.id,
  brand_name: brand.name,
  l1_category_id: l1Category?.id ?? 0,
  l1_category_name: l1Category?.name,
  l2_category_id: l2Category?.id ?? 0,
  l2_category_name: l2Category?.name,
})

type BrandScopeSelectionFlagsType = {
  brandIds: Set<number>
  l1Ids: Set<number>
  l2Ids: Set<number>
  hasBrand: boolean
  hasL1: boolean
  hasL2: boolean
}

const buildBrandScopeSelectionFlags = (
  selection: BrandScopeSelectionType,
): BrandScopeSelectionFlagsType => ({
  brandIds: toBrandIdSet(selection.brands),
  l1Ids: toScopeIdSet(selection.l1Categories),
  l2Ids: toScopeIdSet(selection.l2Categories),
  hasBrand: selection.brands.length > 0,
  hasL1: selection.l1Categories.length > 0,
  hasL2: selection.l2Categories.length > 0,
})

const buildCombinationConfig = (
  hasBrand: boolean,
  brand: PurchaseTermsNamedItemType,
  l1Category: RuleGroupInactiveFilterItemType | null,
  l2Category: RuleGroupInactiveFilterItemType | null,
): RuleGroupConfigType | null => {
  const config: RuleGroupConfigType = {
    id: 0,
    brand_id: hasBrand ? brand.id : 0,
    brand_name: hasBrand ? brand.name : undefined,
    l1_category_id: l1Category?.id ?? 0,
    l1_category_name: l1Category?.name,
    l2_category_id: l2Category?.id ?? 0,
    l2_category_name: l2Category?.name,
  }

  if (!config.brand_id && !config.l1_category_id && !config.l2_category_id) {
    return null
  }

  return config
}

const buildBrandConfigCombinations = (
  selection: BrandScopeSelectionType,
): RuleGroupConfigType[] => {
  const flags = buildBrandScopeSelectionFlags(selection)

  if (!flags.hasBrand && !flags.hasL1 && !flags.hasL2) {
    return []
  }

  if (flags.hasBrand && !flags.hasL1 && !flags.hasL2) {
    return selection.brands.map((brand) => buildConfigTuple(brand, null, null))
  }

  const brandList = flags.hasBrand ? selection.brands : [{ id: 0, name: '' }]
  const l1List = flags.hasL1 ? selection.l1Categories : [null]
  const l2List = flags.hasL2 ? selection.l2Categories : [null]
  const configs: RuleGroupConfigType[] = []

  brandList.forEach((brand) => {
    l1List.forEach((l1Category) => {
      l2List.forEach((l2Category) => {
        const config = buildCombinationConfig(flags.hasBrand, brand, l1Category, l2Category)
        if (config) {
          configs.push(config)
        }
      })
    })
  })

  return configs
}

const isL1SelectedForPath = (path: CatalogPathType, l1Ids: Set<number>) => l1Ids.has(path.l1.id)

const isL2SelectedForPath = (path: CatalogPathType, l2Ids: Set<number>) =>
  Boolean(path.l2 && l2Ids.has(path.l2.id))

const anyL2SelectedForBrandL1 = (
  paths: CatalogPathType[],
  path: CatalogPathType,
  l2Ids: Set<number>,
) =>
  paths.some(
    (candidate) =>
      candidate.brand.id === path.brand.id &&
      candidate.l1.id === path.l1.id &&
      candidate.l2 !== null &&
      isL2SelectedForPath(candidate, l2Ids),
  )

const anyCategorySelectedUnderBrand = (
  paths: CatalogPathType[],
  brandId: number,
  l1Ids: Set<number>,
  l2Ids: Set<number>,
) =>
  paths.some(
    (path) =>
      path.brand.id === brandId &&
      (isL1SelectedForPath(path, l1Ids) || isL2SelectedForPath(path, l2Ids)),
  )

const storeConfig = (
  configsByKey: Map<string, RuleGroupConfigType>,
  config: RuleGroupConfigType,
) => {
  configsByKey.set(brandScopeConfigKey(config), config)
}

const resolveL2OnlyCatalogPathConfig = (
  path: CatalogPathType,
  l2On: boolean,
): RuleGroupConfigType | null => {
  if (!l2On || !path.l2) {
    return null
  }

  return buildCombinationConfig(false, path.brand, null, path.l2)
}

const resolveL1CatalogPathConfig = (
  path: CatalogPathType,
  paths: CatalogPathType[],
  flags: BrandScopeSelectionFlagsType,
  l1On: boolean,
): RuleGroupConfigType | null => {
  if (flags.hasL1 && !l1On) {
    return null
  }

  if (flags.hasBrand && flags.hasL1 && (!flags.brandIds.has(path.brand.id) || !l1On)) {
    return null
  }

  if (anyL2SelectedForBrandL1(paths, path, flags.l2Ids)) {
    return null
  }

  return buildCombinationConfig(flags.hasBrand, path.brand, l1On ? path.l1 : null, null)
}

const resolveCatalogPathConfig = (
  path: CatalogPathType,
  paths: CatalogPathType[],
  flags: BrandScopeSelectionFlagsType,
): RuleGroupConfigType | null => {
  if (flags.hasBrand && !flags.brandIds.has(path.brand.id)) {
    return null
  }

  const l1On = isL1SelectedForPath(path, flags.l1Ids)
  const l2On = isL2SelectedForPath(path, flags.l2Ids)

  if (flags.hasL2 && !flags.hasL1 && !flags.hasBrand) {
    return resolveL2OnlyCatalogPathConfig(path, l2On)
  }

  if (l2On && path.l2) {
    return buildCombinationConfig(
      flags.hasBrand,
      path.brand,
      flags.hasL1 && l1On ? path.l1 : null,
      path.l2,
    )
  }

  if (flags.hasL2 && !flags.hasL1) {
    return null
  }

  return resolveL1CatalogPathConfig(path, paths, flags, l1On)
}

const shouldSkipBrandOnlyFallback = (
  brand: PurchaseTermsNamedItemType,
  paths: CatalogPathType[],
  flags: BrandScopeSelectionFlagsType,
) => {
  if (anyCategorySelectedUnderBrand(paths, brand.id, flags.l1Ids, flags.l2Ids)) {
    return true
  }

  if (!flags.hasL2 || flags.hasL1) {
    return false
  }

  return paths.some((path) => path.brand.id === brand.id && path.l2 && flags.l2Ids.has(path.l2.id))
}

const appendBrandOnlyConfigs = (
  brands: PurchaseTermsNamedItemType[],
  paths: CatalogPathType[],
  flags: BrandScopeSelectionFlagsType,
  configsByKey: Map<string, RuleGroupConfigType>,
) => {
  brands.forEach((brand) => {
    if (shouldSkipBrandOnlyFallback(brand, paths, flags)) {
      return
    }

    storeConfig(configsByKey, buildConfigTuple(brand, null, null))
  })
}

export const buildBrandScopeConfigsFromCatalog = (
  catalog: BrandCategoriesApiItemType[],
  selection: BrandScopeSelectionType,
): RuleGroupConfigType[] => {
  if (!catalog.length) {
    return buildBrandConfigCombinations(selection)
  }

  const flags = buildBrandScopeSelectionFlags(selection)

  if (!flags.hasBrand && !flags.hasL1 && !flags.hasL2) {
    return []
  }

  if (flags.hasBrand && !flags.hasL1 && !flags.hasL2) {
    return selection.brands.map((brand) => buildConfigTuple(brand, null, null))
  }

  const paths = buildCatalogPaths(catalog)
  const configsByKey = new Map<string, RuleGroupConfigType>()

  paths.forEach((path) => {
    const config = resolveCatalogPathConfig(path, paths, flags)
    if (config) {
      storeConfig(configsByKey, config)
    }
  })

  if (flags.hasBrand) {
    appendBrandOnlyConfigs(selection.brands, paths, flags, configsByKey)
  }

  return Array.from(configsByKey.values())
}

export const getApprovedBrandScopeConfigs = (
  configs: RuleGroupConfigType[] | undefined,
): RuleGroupConfigType[] =>
  (configs ?? [])
    .filter((config) => (config.id ?? 0) > 0)
    .map((config) => {
      const persistedConfig = { ...config }
      delete persistedConfig.marked_for_deletion
      return persistedConfig
    })

const toStoredConfigTuple = (config: RuleGroupConfigType): RuleGroupConfigType => ({
  id: config.id ?? 0,
  brand_id: config.brand_id ?? 0,
  l1_category_id: config.l1_category_id ?? 0,
  l2_category_id: config.l2_category_id ?? 0,
})

const withOptionalConfigNames = (
  tuple: RuleGroupConfigType,
  source: RuleGroupConfigType,
  nextConfig?: RuleGroupConfigType,
): RuleGroupConfigType => ({
  ...tuple,
  brand_name: nextConfig?.brand_name ?? source.brand_name,
  l1_category_name: nextConfig?.l1_category_name ?? source.l1_category_name,
  l2_category_name: nextConfig?.l2_category_name ?? source.l2_category_name,
})

export const diffBrandScopeConfigs = (
  storedConfigs: RuleGroupConfigType[],
  nextConfigs: RuleGroupConfigType[],
): RuleGroupConfigType[] => {
  const nextByKey = new Map(
    nextConfigs.map((config) => [brandScopeConfigKey(config), config] as const),
  )
  const nextKeys = new Set(nextByKey.keys())
  const storedKeys = new Set(storedConfigs.map((config) => brandScopeConfigKey(config)))

  const fromStored = storedConfigs.map((stored) => {
    const key = brandScopeConfigKey(stored)
    const tuple = toStoredConfigTuple(stored)
    const nextMatch = nextByKey.get(key)

    if (nextKeys.has(key)) {
      return withOptionalConfigNames(tuple, stored, nextMatch)
    }

    return {
      ...withOptionalConfigNames(tuple, stored),
      marked_for_deletion: true,
    }
  })

  const inserts = nextConfigs.filter((config) => !storedKeys.has(brandScopeConfigKey(config)))

  return [...fromStored, ...inserts]
}

export const removeL1CategoryFromSelection = (
  catalog: BrandCategoriesApiItemType[],
  selection: BrandScopeSelectionType,
  removedL1Id: number,
): BrandScopeSelectionType => {
  const l2IdsUnderRemovedL1 = new Set<number>()

  catalog.forEach((brand) => {
    brand.categories.forEach((l1Category) => {
      if (l1Category.category_id !== removedL1Id) return
      l1Category.subcategories?.forEach((l2Category) => {
        l2IdsUnderRemovedL1.add(l2Category.category_id)
      })
    })
  })

  return {
    ...selection,
    l1Categories: selection.l1Categories.filter((category) => category.id !== removedL1Id),
    l2Categories: selection.l2Categories.filter(
      (category) => category.id == null || !l2IdsUnderRemovedL1.has(category.id),
    ),
  }
}

export const applyL1CategoriesChangeWithCascade = (
  catalog: BrandCategoriesApiItemType[],
  selection: BrandScopeSelectionType,
  nextL1Categories: PurchaseTermsNamedItemType[],
): BrandScopeSelectionType => {
  const nextL1IdSet = new Set(nextL1Categories.map((item) => item.id))
  const removedL1Ids = selection.l1Categories
    .map((item) => item.id)
    .filter((id): id is number => id != null && id > 0 && !nextL1IdSet.has(id))

  const initialScope: BrandScopeSelectionType = {
    ...selection,
    l1Categories: nextL1Categories.map((item) => ({ id: item.id, name: item.name })),
  }

  return removedL1Ids.reduce(
    (currentScope, l1Id) => removeL1CategoryFromSelection(catalog, currentScope, l1Id),
    initialScope,
  )
}

export const applyBrandsChangeWithCascade = (
  catalog: BrandCategoriesApiItemType[],
  selection: BrandScopeSelectionType,
  nextBrands: PurchaseTermsNamedItemType[],
): BrandScopeSelectionType => {
  const nextBrandIds = toBrandIdSet(nextBrands)
  const removedBrandIds = selection.brands
    .map((item) => item.id)
    .filter((id): id is number => id != null && id > 0 && !nextBrandIds.has(id))

  if (removedBrandIds.length === 0) {
    return {
      ...selection,
      brands: nextBrands,
    }
  }

  const paths = buildCatalogPaths(catalog)
  const hasBrand = nextBrands.length > 0

  const prunedL1Categories = selection.l1Categories.filter((item) => {
    const l1Id = item.id
    if (l1Id == null || l1Id <= 0) return false
    return paths.some(
      (path) => path.l1.id === l1Id && (!hasBrand || nextBrandIds.has(path.brand.id)),
    )
  })
  const prunedL1Ids = toScopeIdSet(prunedL1Categories)

  const prunedL2Categories = selection.l2Categories.filter((item) => {
    const l2Id = item.id
    if (l2Id == null || l2Id <= 0) return false
    return paths.some(
      (path) =>
        Boolean(path.l2 && path.l2.id === l2Id) &&
        (!hasBrand || nextBrandIds.has(path.brand.id)) &&
        (prunedL1Categories.length === 0 || prunedL1Ids.has(path.l1.id)),
    )
  })

  return {
    brands: nextBrands,
    l1Categories: uniqueScopeFilterItems(
      prunedL1Categories.map((item) => ({ id: item.id, name: item.name })),
    ),
    l2Categories: uniqueScopeFilterItems(
      prunedL2Categories.map((item) => ({ id: item.id, name: item.name })),
    ),
  }
}
